import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { BrandCategoriesStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { BrandCategoryModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandDetailsCategoriesStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    categoryContainer?: InjectableCss;
    categoryLink?: LinkPresentationProps;
}

export const categoriesStyles: BrandDetailsCategoriesStyles = {
    container: {
        css: css`
            width: 350px;
            margin: 0 auto;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                ]);
            }}
        `,
    },
    title: {
        transform: "uppercase",
        weight: 800,
        size: 13,
        css: css`
            color: ${UCSColors.gray04} !important;
            line-height: 16px;
            letter-spacing: 0.1em;
            margin-bottom: 7px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                        margin-bottom: 2px;
                    `,
                    css`
                        font-size: 12px;
                        margin-bottom: 2px;
                    `,
                ]);
            }}
        `,
    },
    categoryContainer: {
        css: css`
            margin-top: 16px;
        `,
    },
    categoryLink: {
        typographyProps: {
            size: 18,
            css: css`
                margin-right: 4px;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 17px;
                        `,
                        css`
                            font-size: 17px;
                        `,
                    ]);
                }}
            `,
        },
        color: "primary",
    },
};

const styles = categoriesStyles;

const BrandDetailsCategories: FC<Props> = ({ fields }) => {
    const { isLoading, value: brandCategories } = useContext(BrandCategoriesStateContext);
    if (isLoading || !brandCategories) {
        return null;
    }

    const { title } = fields;
    const categoriesToDisplay: BrandCategoryModel[] = [];

    const getCategory = (cat: BrandCategoryModel) => {
        if (cat.subCategories && cat.subCategories.length > 0) {
            cat.subCategories.forEach(c => {
                getCategory(c);
            });
        } else {
            categoriesToDisplay.push(cat);
        }
    };

    const filterCategories = () => {
        brandCategories.forEach(cat => {
            if (cat.subCategories && cat.subCategories.length > 0) {
                getCategory(cat);
            }
        });
    };

    filterCategories();

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandCategories">
            <Typography {...styles.title} data-test-selector="brandCategoriesTitle">
                {title}
            </Typography>
            <StyledWrapper {...styles.categoryContainer}>
                {categoriesToDisplay
                    .sort((a, b) => a.categoryName.localeCompare(b.categoryName))
                    .map((category, idx) => (
                        <Link key={category.categoryName} href={category.productListPagePath} {...styles.categoryLink}>
                            {idx + 1 === categoriesToDisplay.length
                                ? category.categoryName
                                : `${category.categoryName},`}
                        </Link>
                    ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandDetailsCategories),
    definition: {
        group: "Brand Details",
        displayName: "Categories",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Shop By Category",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
